import React, { createContext, useContext, useState, useEffect } from 'react';
import { Cart, CartItem } from '../types/store';
import { InventoryItem } from '../utils/types';
import { calculateProductDiscount } from '../services/promotionService';

interface CartContextType {
  cart: Cart;
  addToCart: (product: InventoryItem, quantity: number) => Promise<void>;
  removeFromCart: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number) => Promise<void>;
  clearCart: () => void;
  isInCart: (productId: string) => boolean;
}

const defaultCart: Cart = {
  items: [],
  totalAmount: 0,
  totalItems: 0
};

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [cart, setCart] = useState<Cart>(() => {
    // استرجاع سلة التسوق من التخزين المحلي عند بدء التطبيق
    const savedCart = localStorage.getItem('cart');
    return savedCart ? JSON.parse(savedCart) : defaultCart;
  });

  // حفظ سلة التسوق في التخزين المحلي عند تغييرها
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cart));
  }, [cart]);

  const calculateTotals = (items: CartItem[]): { totalAmount: number; totalItems: number } => {
    return items.reduce(
      (acc, item) => {
        const itemTotal = item.product.sellingPrice * item.quantity;
        return {
          totalAmount: acc.totalAmount + itemTotal,
          totalItems: acc.totalItems + item.quantity
        };
      },
      { totalAmount: 0, totalItems: 0 }
    );
  };

  const addToCart = async (product: InventoryItem, quantity: number) => {
    // حساب الخصم بناءً على الكمية
    const discount = await calculateProductDiscount(product, quantity);
    const finalProduct = discount ? { 
      ...product, 
      originalPrice: (product as any).originalPrice || product.sellingPrice,
      sellingPrice: discount.finalPrice,
      onSale: true,
      discount
    } : {
      ...product,
      sellingPrice: (product as any).originalPrice || product.sellingPrice,
      onSale: false,
      discount: undefined
    };

    setCart((prevCart) => {
      const existingItemIndex = prevCart.items.findIndex(
        (item) => item.productId === product.id
      );

      let updatedItems: CartItem[];

      if (existingItemIndex >= 0) {
        const newQuantity = prevCart.items[existingItemIndex].quantity + quantity;
        updatedItems = [...prevCart.items];
        updatedItems[existingItemIndex] = {
          ...updatedItems[existingItemIndex],
          product: finalProduct,
          quantity: newQuantity
        };
      } else {
        updatedItems = [
          ...prevCart.items,
          {
            productId: product.id,
            product: finalProduct,
            quantity
          }
        ];
      }

      const { totalAmount, totalItems } = calculateTotals(updatedItems);

      return {
        ...prevCart,
        items: updatedItems,
        totalAmount,
        totalItems
      };
    });
  };

  const removeFromCart = (productId: string) => {
    setCart((prevCart) => {
      const updatedItems = prevCart.items.filter(
        (item) => item.productId !== productId
      );
      
      const { totalAmount, totalItems } = calculateTotals(updatedItems);

      return {
        ...prevCart,
        items: updatedItems,
        totalAmount,
        totalItems
      };
    });
  };

  const updateQuantity = async (productId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }

    // جلب المنتج الحالي
    const currentItem = cart.items.find(item => item.productId === productId);
    if (!currentItem) return;

    // حساب الخصم بناءً على الكمية الجديدة
    const discount = await calculateProductDiscount(currentItem.product, quantity);
    const updatedProduct = discount ? {
        ...currentItem.product,
        originalPrice: (currentItem.product as any).originalPrice || currentItem.product.sellingPrice,
        sellingPrice: discount.finalPrice,
        onSale: true,
        discount
    } : {
        ...currentItem.product,
        sellingPrice: (currentItem.product as any).originalPrice || currentItem.product.sellingPrice,
        onSale: false,
        discount: undefined
    };

    setCart((prevCart) => {
      const updatedItems = prevCart.items.map((item) =>
        item.productId === productId
          ? { ...item, quantity, product: updatedProduct }
          : item
      );
      
      const { totalAmount, totalItems } = calculateTotals(updatedItems);

      return {
        ...prevCart,
        items: updatedItems,
        totalAmount,
        totalItems
      };
    });
  };

  const clearCart = () => {
    setCart(defaultCart);
  };

  const isInCart = (productId: string) => {
    return cart.items.some((item) => item.productId === productId);
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        isInCart
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
